/* ============================================================================
   EL MOTOR DE SINCRONIZACIÓN — del lado del dispositivo

   ═══ EL LIBRO DE VUELO SE ESCRIBE PRIMERO EN EL DISPOSITIVO ═══

   Ninguna operación de este archivo espera a la red para dejar guardado un
   vuelo. El motor lee lo local, detecta qué cambió, lo encola y, cuando hay
   conexión, lo manda. Si no hay conexión, la cola queda y la pantalla lo dice.

   ═══ EL SERVIDOR DECIDE QUIÉN GANA, PERO EL PILOTO PUEDE CORREGIRLO ═══

   El servidor aplica «el último gana» por marca de tiempo. Cuando el cambio del
   dispositivo pierde (`descartados`), acá NO se pisa el dato local: se guarda el
   conflicto con las dos versiones y se le pregunta al piloto. Elegir la propia
   se manda con `confirmado: true`, que el servidor sella de nuevo (e145).

   ═══ UN RELOJ MAL NO SE CORRIGE SOLO ═══

   La marca que decide es la del dispositivo. Un iPad adelantado gana siempre y
   pisa a los demás sin que nadie lo note. Se compara el reloj del servidor con
   el local y se avisa; no se ajusta nada por cuenta propia, porque mover las
   marcas de un vuelo ya registrado es peor que avisar.
   ========================================================================== */

import { ENTIDADES, claveDeEntidad, entidadPorNombre } from "./entidades.js";
import { huella } from "./huella.js";
import { diferenciasDeLista, encolarCambio, quitarDeLaCola, tandaDeEnvio } from "./cola.js";

export const CLAVE_COLA = "sync:cola";
export const CLAVE_CONFLICTOS = "sync:conflictos";
export const CLAVE_RECHAZADOS = "sync:rechazados";
export const CLAVE_ESTADO = "sync:estado";
export const TOLERANCIA_RELOJ_MS = 5 * 60 * 1000;

export const metaKey = (entidad) => `sync:meta:${entidad}`;

export function metaVacia() {
    return { desde: 0, cursor: null, base: {}, conocidos: {}, marcas: {} };
}

export function estadoVacio() {
    return {
        fase: "inactivo",
        pendientes: 0,
        conflictos: 0,
        rechazados: 0,
        ultimaVez: null,
        desfaseMs: 0,
        detalle: null,
        entidades: {},
    };
}

function baseDe(registro) {
    if (!registro) return null;
    return {
        en: Number(registro.actualizado_en) || 0,
        huella: registro.borrado_en != null ? null : huella(registro.datos),
    };
}

function mensajeDe(error) {
    return String(error?.message || error || "Error de sincronización.");
}

export function crearMotor({
    almacen,
    transporte,
    ahora = () => Date.now(),
    entidades = ENTIDADES,
    avisar = () => {},
    puedeLeer = () => true,
    puedeEscribir = () => true,
} = {}) {
    if (!almacen) throw new Error("El motor necesita un almacén.");
    if (!transporte) throw new Error("El motor necesita un transporte.");

    const oyentes = new Set();
    let ejecutando = null;
    let turno = Promise.resolve();

    /* ═══ UNA MUTACIÓN A LA VEZ ═══
       La cola y los metadatos son un read-modify-write. Si la pantalla encola
       una edición mientras el motor está quitando lo enviado, la última
       escritura pisa a la otra y el cambio desaparece. Todas las mutaciones
       pasan por esta cola, igual que las escrituras de `usePersistedState`. */
    function exclusiva(tarea) {
        const siguiente = turno.then(tarea, tarea);
        turno = siguiente.catch(() => {});
        return siguiente;
    }

    const leer = async (clave) => {
        try { return await almacen.leer(clave); } catch { return undefined; }
    };
    const guardar = async (clave, valor) => {
        try { await almacen.guardar(clave, valor); return true; } catch { return false; }
    };

    async function leerCola() {
        const cola = await leer(CLAVE_COLA);
        return Array.isArray(cola) ? cola : [];
    }

    async function leerConflictos() {
        const lista = await leer(CLAVE_CONFLICTOS);
        return Array.isArray(lista) ? lista : [];
    }

    async function leerRechazados() {
        const lista = await leer(CLAVE_RECHAZADOS);
        return Array.isArray(lista) ? lista : [];
    }

    async function leerMeta(nombre) {
        const meta = await leer(metaKey(nombre));
        if (!meta || typeof meta !== "object") return metaVacia();
        return {
            ...metaVacia(),
            ...meta,
            base: meta.base && typeof meta.base === "object" ? meta.base : {},
            conocidos: meta.conocidos && typeof meta.conocidos === "object" ? meta.conocidos : {},
            marcas: meta.marcas && typeof meta.marcas === "object" ? meta.marcas : {},
        };
    }

    async function publicar(cambios) {
        const previo = (await leer(CLAVE_ESTADO)) || estadoVacio();
        const siguiente = { ...estadoVacio(), ...previo, ...cambios, actualizadoEn: ahora() };
        await guardar(CLAVE_ESTADO, siguiente);
        for (const oyente of oyentes) {
            try { oyente(siguiente); } catch { /* un oyente roto no frena el motor */ }
        }
        return siguiente;
    }

    async function estadoActual() {
        const estado = (await leer(CLAVE_ESTADO)) || estadoVacio();
        const cola = await leerCola();
        const conflictos = await leerConflictos();
        const rechazados = await leerRechazados();
        return {
            ...estadoVacio(),
            ...estado,
            pendientes: cola.length,
            conflictos: conflictos.length,
            rechazados: rechazados.length,
        };
    }

    function suscribir(oyente) {
        oyentes.add(oyente);
        return () => oyentes.delete(oyente);
    }

    /* ───────────────────────────────────────────────────────────────────────
     * LO LOCAL
     * ────────────────────────────────────────────────────────────────────── */

    /**
     * Asigna id a los registros viejos.
     *
     * La aplicación guardó filas sin `id` durante entregas anteriores. El
     * servidor exige un id estable para poder decidir quién gana; inventarlo en
     * cada sincronización crearía un registro nuevo por vuelta. Se usa la
     * huella del contenido y la posición: estable mientras el registro no se
     * edite, y la edición ya lo convierte en otro registro a los ojos del
     * servidor.
     */
    function conIds(registros) {
        let cambio = false;
        const salida = registros.map((registro, indice) => {
            if (!registro || typeof registro !== "object" || registro.id) return registro;
            cambio = true;
            return { ...registro, id: `local_${huella({ registro, indice })}` };
        });
        return { registros: salida, cambio };
    }

    async function leerLista(entidad) {
        const valor = await leer(claveDeEntidad(entidad));
        return Array.isArray(valor) ? valor : [];
    }

    async function detectarCambios(nombre) {
        const entidad = entidadPorNombre(nombre);
        if (!entidad) return 0;

        /* Un piloto no escribe la flota. Sin esta comprobación, la flota de
           fábrica que el dispositivo ya tiene se encolaría en cada arranque y
           el servidor la rechazaría: ruido que parece un error de datos. El
           servidor sigue siendo el que decide; esto solo evita pedirle permiso
           para algo que la matriz ya niega. */
        if (!puedeEscribir(nombre)) return 0;

        let local = await leerLista(entidad);
        const conIdentidad = conIds(local);
        if (conIdentidad.cambio) {
            local = conIdentidad.registros;
            await guardar(claveDeEntidad(entidad), local);
            avisar(entidad.clave);
        }

        const meta = await leerMeta(nombre);
        const colaInicial = await leerCola();
        let cola = colaInicial;
        let metaCambio = false;
        const marca = ahora();
        const presentes = new Set();

        for (const registro of local) {
            const id = registro?.id == null ? "" : String(registro.id);
            if (!id) continue;
            presentes.add(id);
            const h = huella(registro);
            if (meta.conocidos[id] === h) continue;

            /* Si coincide con lo último que el servidor confirmó, no es una
               edición local: es el mismo dato releído. */
            if (meta.base[id] && meta.base[id].huella === h) {
                meta.conocidos[id] = h;
                metaCambio = true;
                continue;
            }

            cola = encolarCambio(cola, {
                entidad: nombre, id, datos: registro, actualizado_en: marca,
            });
            meta.conocidos[id] = h;
            meta.marcas[id] = marca;
            metaCambio = true;
        }

        /* Lo que estaba y ya no está: una baja. */
        for (const id of Object.keys(meta.conocidos)) {
            if (presentes.has(id)) continue;
            if (meta.base[id] && meta.base[id].huella === null) {
                delete meta.conocidos[id];
                delete meta.marcas[id];
                metaCambio = true;
                continue;
            }
            cola = encolarCambio(cola, {
                entidad: nombre, id, datos: {}, actualizado_en: marca, borrado_en: marca,
            });
            delete meta.conocidos[id];
            delete meta.marcas[id];
            metaCambio = true;
        }

        if (cola !== colaInicial) await guardar(CLAVE_COLA, cola);
        if (metaCambio) await guardar(metaKey(nombre), meta);
        return cola.length - colaInicial.length;
    }

    /**
     * La pantalla avisa qué cambió, sin releer el almacén.
     *
     * `usePersistedState` ya tiene el valor anterior y el nuevo en la mano. Si
     * el motor leyera IndexedDB en ese instante, podría encontrar la escritura
     * anterior todavía en vuelo y encolar el valor viejo. La diferencia se
     * calcula en memoria.
     */
    async function registrarDiferencia(nombre, antes, despues) {
        const entidad = entidadPorNombre(nombre);
        if (!entidad || !puedeEscribir(nombre)) return;

        return exclusiva(async () => {
            const marca = ahora();
            const cambios = diferenciasDeLista(antes, despues, { entidad: nombre, ahora: marca });
            if (!cambios.length) return;

            const meta = await leerMeta(nombre);
            let cola = await leerCola();

            for (const cambio of cambios) {
                const id = String(cambio.id);
                if (cambio.borrado_en != null) {
                    /* Una fila que nació y murió sin llegar al servidor no
                       necesita una baja: nadie la conoce. */
                    if (!(id in meta.conocidos) && !meta.base[id]) continue;
                    cola = encolarCambio(cola, cambio);
                    delete meta.conocidos[id];
                    delete meta.marcas[id];
                    continue;
                }

                const h = huella(cambio.datos);
                if (meta.conocidos[id] === h) continue;
                cola = encolarCambio(cola, cambio);
                meta.conocidos[id] = h;
                meta.marcas[id] = cambio.actualizado_en;
            }

            await guardar(CLAVE_COLA, cola);
            await guardar(metaKey(nombre), meta);
            await publicar({ pendientes: cola.length });
        });
    }

    /* ───────────────────────────────────────────────────────────────────────
     * LO DEL SERVIDOR
     * ────────────────────────────────────────────────────────────────────── */

    function conflictoDe(nombre, propio, servidor) {
        return {
            id: `${nombre}:${propio?.id ?? servidor?.id ?? ""}`,
            entidad: nombre,
            entidadId: String(propio?.id ?? servidor?.id ?? ""),
            propio: propio ? {
                datos: propio.datos,
                actualizado_en: propio.actualizado_en,
                borrado_en: propio.borrado_en ?? null,
            } : null,
            servidor: servidor ? {
                datos: servidor.datos,
                actualizado_en: servidor.actualizado_en,
                borrado_en: servidor.borrado_en ?? null,
            } : null,
            detectadoEn: ahora(),
        };
    }

    async function guardarConflicto(conflicto) {
        const lista = await leerConflictos();
        const indice = lista.findIndex((c) => c.id === conflicto.id);
        if (indice < 0) lista.push(conflicto);
        else lista[indice] = { ...lista[indice], ...conflicto };
        await guardar(CLAVE_CONFLICTOS, lista);
        return lista;
    }

    async function quitarConflicto(id) {
        const lista = await leerConflictos();
        const siguiente = lista.filter((c) => c.id !== id);
        await guardar(CLAVE_CONFLICTOS, siguiente);
        return siguiente;
    }

    async function anotarRechazos(rechazos) {
        if (!rechazos.length) return;
        const lista = await leerRechazados();
        const porId = new Map(lista.map((r) => [`${r.entidad}:${r.id}`, r]));
        for (const r of rechazos) porId.set(`${r.entidad}:${r.id}`, r);
        await guardar(CLAVE_RECHAZADOS, [...porId.values()]);
    }

    /**
     * Aplica registros del servidor a una entidad.
     *
     * `forzar` se usa para la respuesta de un envío y para la decisión del
     * piloto: el servidor ya dijo cuál es el estado, no hay nada que comparar.
     */
    async function aplicarRegistros(nombre, registros, { forzar = false } = {}) {
        const entidad = entidadPorNombre(nombre);
        if (!entidad || !registros.length) return { aplicados: 0, conflictos: 0 };

        let local = await leerLista(entidad);
        const meta = await leerMeta(nombre);
        const cola = await leerCola();
        const enCola = new Set(cola.filter((c) => c.entidad === nombre).map((c) => String(c.id)));
        const conflictos = await leerConflictos();
        const conflictoPorId = new Map(
            conflictos.filter((c) => c.entidad === nombre).map((c) => [c.entidadId, c]),
        );
        const conflictosTocados = new Set();

        const nuevos = [];
        const porId = new Map(local.map((r, i) => [String(r?.id), i]));
        const reemplazos = new Map();
        const bajas = new Set();
        let aplicados = 0;

        for (const registro of registros) {
            const id = String(registro?.id ?? "");
            if (!id) continue;

            const indice = porId.get(id);
            const existente = indice === undefined ? null : local[indice];
            const base = meta.base[id];
            const cambioDelServidor = forzar || !base || Number(registro.actualizado_en) > Number(base.en);

            if (!cambioDelServidor) continue;

            const conflictoPendiente = conflictoPorId.get(id);
            const localEditado = enCola.has(id);

            if (conflictoPendiente && !forzar) {
                /* El servidor volvió a cambiar mientras el piloto decide. Se
                   actualiza la versión del servidor, no se pisa la local. */
                if (Number(registro.actualizado_en) > Number(conflictoPendiente.servidor?.actualizado_en || 0)) {
                    conflictoPendiente.servidor = {
                        datos: registro.datos,
                        actualizado_en: registro.actualizado_en,
                        borrado_en: registro.borrado_en ?? null,
                    };
                    conflictosTocados.add(id);
                }
                meta.base[id] = baseDe(registro);
                continue;
            }

            if (localEditado && existente && !forzar) {
                const conflicto = conflictoDe(nombre, {
                    id,
                    datos: existente,
                    actualizado_en: meta.marcas[id] || ahora(),
                    borrado_en: null,
                }, registro);
                conflictoPorId.set(id, conflicto);
                conflictosTocados.add(id);
                meta.base[id] = baseDe(registro);
                continue;
            }

            if (registro.borrado_en != null) {
                if (existente) bajas.add(id);
            } else if (existente) {
                reemplazos.set(id, registro.datos);
            } else {
                nuevos.push(registro.datos);
            }

            meta.base[id] = baseDe(registro);
            if (registro.borrado_en != null) {
                delete meta.conocidos[id];
                delete meta.marcas[id];
            } else {
                meta.conocidos[id] = huella(registro.datos);
                meta.marcas[id] = registro.actualizado_en;
            }
            aplicados += 1;
        }

        if (aplicados) {
            local = local
                .filter((r) => !bajas.has(String(r?.id)))
                .map((r) => (reemplazos.has(String(r?.id)) ? reemplazos.get(String(r?.id)) : r));

            if (nuevos.length) {
                local = entidad.orden === "recientes"
                    ? [...nuevos.slice().reverse(), ...local]
                    : [...local, ...nuevos];
            }

            await guardar(claveDeEntidad(entidad), local);
            avisar(entidad.clave);
        }

        if (aplicados || conflictosTocados.size) await guardar(metaKey(nombre), meta);

        if (conflictosTocados.size) {
            /* Se conservan los conflictos de las demás entidades: reemplazar la
               lista entera con los de esta borraría lo que el piloto todavía
               no decidió en otra pantalla. */
            const porId = new Map(conflictos.map((c) => [c.id, c]));
            for (const id of conflictosTocados) {
                const conflicto = conflictoPorId.get(id);
                if (conflicto) porId.set(conflicto.id, conflicto);
            }
            await guardar(CLAVE_CONFLICTOS, [...porId.values()]);
        }

        return { aplicados, conflictos: conflictosTocados.size };
    }

    /** Deja anotado el estado del servidor sin tocar el registro local. Se usa
        cuando el cambio del dispositivo perdió: el conflicto queda pendiente y
        la próxima lectura no debe pisar la versión que el piloto está viendo. */
    async function anotarBaseDeServidor(nombre, registro) {
        if (!registro) return;
        const meta = await leerMeta(nombre);
        meta.base[String(registro.id)] = baseDe(registro);
        await guardar(metaKey(nombre), meta);
    }

    async function procesarEnvio(tanda, datos) {
        const resultados = Array.isArray(datos?.resultados) ? datos.resultados : [];
        const idsProcesados = new Map();
        const rechazos = [];
        let conflictos = 0;

        for (const resultado of resultados) {
            const nombre = resultado?.entidad;
            if (!entidadPorNombre(nombre)) continue;
            const propios = new Map(
                tanda.filter((c) => c.entidad === nombre).map((c) => [String(c.id), c]),
            );
            const registros = new Map(
                (resultado.registros || []).map((r) => [String(r.id), r]),
            );

            if (!idsProcesados.has(nombre)) idsProcesados.set(nombre, new Set());
            const ids = idsProcesados.get(nombre);

            for (const id of resultado.aplicados || []) {
                ids.add(String(id));
                const registro = registros.get(String(id));
                if (registro) await aplicarRegistros(nombre, [registro], { forzar: true });
                const conflicto = `${nombre}:${id}`;
                const pendientes = await leerConflictos();
                if (pendientes.some((c) => c.id === conflicto)) await quitarConflicto(conflicto);
            }

            for (const id of resultado.descartados || []) {
                ids.add(String(id));
                const registro = registros.get(String(id));
                const propio = propios.get(String(id));
                if (registro && propio) {
                    await guardarConflicto(conflictoDe(nombre, propio, registro));
                    await anotarBaseDeServidor(nombre, registro);
                    conflictos += 1;
                }
            }

            for (const rechazo of resultado.rechazados || []) {
                const id = String(rechazo?.id ?? "");
                if (!id) continue;
                ids.add(id);
                rechazos.push({ entidad: nombre, id, motivo: rechazo.motivo || "Rechazado." });
            }
        }

        if (idsProcesados.size) {
            const cola = await leerCola();
            await guardar(CLAVE_COLA, quitarDeLaCola(cola, idsProcesados));
        }
        if (rechazos.length) await anotarRechazos(rechazos);
        return { conflictos, rechazos: rechazos.length };
    }

    async function traerEntidad(nombre) {
        const entidad = entidadPorNombre(nombre);
        if (!entidad) return { ok: true, sinPermiso: false };
        if (!puedeLeer(nombre)) return { ok: true, sinPermiso: true };
        let meta = await leerMeta(nombre);
        let desde = Number(meta.desde) || 0;
        let cursor = meta.cursor || null;
        let desfase = null;

        for (let pagina = 0; pagina < 200; pagina += 1) {
            const respuesta = await transporte.traer(nombre, { desde, cursor });
            if (!respuesta?.ok) {
                /* 403 no es una falla: es un perfil que no consulta esa
                   entidad. El piloto no lee la flota, y eso está bien. */
                if (respuesta?.estado === 403) return { ok: true, sinPermiso: true };
                return { ok: false, estado: respuesta?.estado, motivo: respuesta?.motivo };
            }

            const datos = respuesta.datos || {};
            if (Number.isFinite(datos.ahora)) desfase = Number(datos.ahora) - ahora();

            const registros = Array.isArray(datos.registros) ? datos.registros : [];
            if (registros.length) await aplicarRegistros(nombre, registros, { forzar: false });

            meta = await leerMeta(nombre);
            if (datos.cursor && Number.isFinite(Number(datos.cursor.actualizado_en))) {
                meta.desde = Number(datos.cursor.actualizado_en);
                meta.cursor = datos.cursor;
                await guardar(metaKey(nombre), meta);
                desde = meta.desde;
                cursor = meta.cursor;
            }

            if (!datos.hayMas) break;
        }

        return { ok: true, sinPermiso: false, desfase };
    }

    async function empujarCola() {
        let vueltas = 0;
        let conflictos = 0;
        let rechazos = 0;

        while (vueltas < 100) {
            vueltas += 1;
            const cola = await leerCola();
            if (!cola.length) break;

            const tanda = tandaDeEnvio(cola);
            const respuesta = await transporte.enviar(tanda);
            if (!respuesta?.ok) {
                return {
                    ok: false,
                    estado: respuesta?.estado,
                    motivo: respuesta?.motivo,
                    conflictos,
                    rechazos,
                };
            }

            const antes = tanda.length;
            const resultado = await procesarEnvio(tanda, respuesta.datos);
            conflictos += resultado.conflictos;
            rechazos += resultado.rechazos;

            /* Si el servidor no nombró ni un id, la cola no avanza y este
               bucle sería infinito. Se corta y se avisa. */
            if (resultado.conflictos === 0 && resultado.rechazos === 0) {
                const despues = await leerCola();
                if (despues.length >= antes) break;
            }
        }

        return { ok: true, conflictos, rechazos };
    }

    /* ───────────────────────────────────────────────────────────────────────
     * EL CICLO
     * ────────────────────────────────────────────────────────────────────── */

    async function ciclo() {
        await publicar({ fase: "sincronizando", detalle: null });

        let desfaseMs = 0;
        let relojMedido = false;
        try {
            /* ═══ LO LOCAL SE DETECTA PRIMERO ═══
               Sin esto, abrir la aplicación sin señal no encola nada: el estado
               del servidor falla y se sale antes de mirar el dispositivo. La
               cola tiene que existir aunque el servidor no esté, porque es lo
               que permite contarlo cuando vuelva. */
            for (const entidad of entidades) await detectarCambios(entidad.nombre);

            /* El estado del servidor no es obligatorio: un piloto no tiene
               `empresa.leer` y recibe 403. Se usa para el reloj cuando está. */
            const estadoServidor = await transporte.estado?.();
            if (estadoServidor?.ok) {
                const ahoraServidor = Number(estadoServidor.datos?.ahora);
                if (Number.isFinite(ahoraServidor)) {
                    desfaseMs = ahoraServidor - ahora();
                    relojMedido = true;
                }
            } else if (estadoServidor && [401, 503].includes(estadoServidor.estado)) {
                const cola = await leerCola();
                const estado = await publicar({
                    fase: "sin-servidor",
                    pendientes: cola.length,
                    detalle: estadoServidor.motivo || "El servidor no está disponible.",
                    desfaseMs: 0,
                });
                return estado;
            }

            const envio = await empujarCola();
            if (!envio.ok) {
                if ([401, 503].includes(envio.estado)) {
                    return publicar({
                        fase: "sin-servidor",
                        detalle: envio.motivo || "El servidor no está disponible.",
                        desfaseMs,
                    });
                }
                return publicar({
                    fase: "error",
                    detalle: envio.motivo || "No se pudo enviar la cola.",
                    desfaseMs,
                });
            }

            for (const entidad of entidades) {
                const traida = await traerEntidad(entidad.nombre);
                if (!traida.ok && [401, 503].includes(traida.estado)) {
                    return publicar({
                        fase: "sin-servidor",
                        detalle: traida.motivo || "El servidor no está disponible.",
                        desfaseMs,
                    });
                }
                if (!relojMedido && Number.isFinite(traida.desfase)) {
                    desfaseMs = traida.desfase;
                    relojMedido = true;
                }
            }

            const cola = await leerCola();
            const conflictos = await leerConflictos();
            const rechazados = await leerRechazados();
            const fase = conflictos.length
                ? "conflicto"
                : cola.length
                    ? "pendiente"
                    : "al-dia";

            return publicar({
                fase,
                pendientes: cola.length,
                conflictos: conflictos.length,
                rechazados: rechazados.length,
                ultimaVez: ahora(),
                desfaseMs,
            });
        } catch (error) {
            return publicar({
                fase: "error",
                detalle: mensajeDe(error),
                desfaseMs,
            });
        }
    }

    function sincronizar() {
        if (ejecutando) return ejecutando;
        ejecutando = exclusiva(ciclo).finally(() => { ejecutando = null; });
        return ejecutando;
    }

    async function resolverConflicto(id, eleccion) {
        const resultado = await exclusiva(async () => {
            const conflictos = await leerConflictos();
            const conflicto = conflictos.find((c) => c.id === id);
            if (!conflicto) return estadoActual();

            if (eleccion === "servidor") {
                if (conflicto.servidor) {
                    await aplicarRegistros(conflicto.entidad, [{
                        id: conflicto.entidadId,
                        ...conflicto.servidor,
                    }], { forzar: true });
                }
                await quitarConflicto(id);
            } else {
                const entidad = entidadPorNombre(conflicto.entidad);
                const meta = await leerMeta(conflicto.entidad);
                const local = entidad ? await leerLista(entidad) : [];
                const actual = local.find((r) => String(r?.id) === conflicto.entidadId);
                const propio = conflicto.propio || {};
                const marca = ahora();

                const confirmado = {
                    entidad: conflicto.entidad,
                    id: conflicto.entidadId,
                    datos: actual || propio.datos || {},
                    actualizado_en: marca,
                    confirmado: true,
                };
                if (propio.borrado_en != null && !actual) confirmado.borrado_en = marca;

                let cola = await leerCola();
                cola = encolarCambio(cola, confirmado);
                await guardar(CLAVE_COLA, cola);

                if (actual) {
                    meta.conocidos[conflicto.entidadId] = huella(actual);
                    meta.marcas[conflicto.entidadId] = marca;
                } else {
                    delete meta.conocidos[conflicto.entidadId];
                    delete meta.marcas[conflicto.entidadId];
                }
                await guardar(metaKey(conflicto.entidad), meta);
                await quitarConflicto(id);
            }

            const restantes = await leerConflictos();
            const cola = await leerCola();
            await publicar({
                fase: restantes.length ? "conflicto" : cola.length ? "pendiente" : "al-dia",
                conflictos: restantes.length,
                pendientes: cola.length,
            });

            return estadoActual();
        });

        /* Fuera de la exclusiva: `sincronizar` toma la misma cola de turnos y
           esperar acá adentro sería un abrazo mortal. */
        if (eleccion !== "servidor") return sincronizar();
        return resultado;
    }

    async function conflictoActual() {
        const lista = await leerConflictos();
        return lista[0] || null;
    }

    async function rechazosActuales() {
        return leerRechazados();
    }

    return {
        sincronizar,
        registrarDiferencia,
        resolverConflicto,
        estadoActual,
        conflictoActual,
        rechazosActuales,
        suscribir,
        detectarCambios,
        aplicarRegistros,
    };
}
